import React from 'react'
import { format, addDays, startOfMonth } from 'date-fns'
import { CircleHelp } from 'lucide-react'
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../../tailwindComponents/ui/dialog'
import { Badge } from '../../../../tailwindComponents/ui/badge'
import { ToggleGroup, ToggleGroupItem } from '../../../../tailwindComponents/ui/toggle-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../tailwindComponents/ui/select'
import {
  Tooltip as HoverTooltip,
  TooltipContent as HoverTooltipContent,
  TooltipProvider as HoverTooltipProvider,
  TooltipTrigger as HoverTooltipTrigger,
} from '../../../../tailwindComponents/ui/tooltip'
import PerformanceDashboardService from '../../../../services/dashboards/PerformanceDashboard.service'
import ReportService from '../../../../services/reports/Report.service'
import { CurrentMonthAdvancedTrendResponse } from '../../../../interfaces/reports/ProgressDashboardReport.interface'

type TrendBasis = 'actuals' | 'plan'
type TrendView = 'cumulative' | 'daily'

interface DynamicTrendPoint {
  day: number;
  dateLabel: string;
  fullDateLabel: string;
  fullDateLabelLastYear: string;
  isElapsed: boolean;
  actual: number | null;
  actualProjected: number | null;
  plan: number;
  lastYear: number;
}

interface DynamicTrendLocationOption {
  value: string;
  label: string;
}

export interface SalesDynamicTrendModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId?: string | number | null;
  epochMonth?: number | null;
  monthLabel?: string;
  locationOptions?: DynamicTrendLocationOption[];
}

const DEFAULT_LOCATION_OPTIONS: DynamicTrendLocationOption[] = [
  { value: 'all', label: 'All locations' },
  { value: 'loc-1', label: 'Location 1' },
  { value: 'loc-2', label: 'Location 2' },
  { value: 'loc-3', label: 'Location 3' },
]

interface CurrentMonthAdvancedTrendDailyPoint {
  locationCode: string;
  locationName: string;
  classCode: string;
  dayOfMonth: number;
  isElapsed: boolean;
  actualDaily: number;
  planDaily: number;
  lastYearDaily: number;
  dailyProjected: number;
  dailyProjectedViaPlan: number;
}

interface CurrentMonthAdvancedTrendPayload {
  rows: CurrentMonthAdvancedTrendDailyPoint[];
}

function formatSignedPercent(value: number, digits = 1) {
  const prefix = value >= 0 ? '+' : ''
  return `${prefix}${value.toFixed(digits)}%`
}

function toFiniteNumber(value: unknown) {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

function resolveCurrentMonthAdvancedTrendPayload(response: any): CurrentMonthAdvancedTrendPayload | null {
  const wrappedData = response?.data
  const payload: CurrentMonthAdvancedTrendResponse | undefined = (wrappedData && !Array.isArray(wrappedData))
    ? wrappedData
    : response

  if (!payload) return null

  const rawDailyRows = Array.isArray(payload.dailyData)
    ? payload.dailyData
    : (Array.isArray(payload.data) ? payload.data : [])

  const rows = rawDailyRows
    .map((row) => ({
      locationCode: String((row as any).locationCode || ''),
      locationName: String((row as any).locationName || ''),
      classCode: String((row as any).classCode || ''),
      dayOfMonth: Math.max(1, Math.floor(toFiniteNumber((row as any).dayOfMonth))),
      isElapsed: Boolean((row as any).isElapsed),
      actualDaily: toFiniteNumber((row as any).actualDaily),
      planDaily: toFiniteNumber((row as any).planDaily),
      lastYearDaily: toFiniteNumber((row as any).lastYearDaily),
      dailyProjected: toFiniteNumber((row as any).dailyProjected ?? (row as any).projectedActuals ?? (row as any).remainingProjectedSalesActualsComponent),
      dailyProjectedViaPlan: toFiniteNumber((row as any).dailyProjectedViaPlan ?? (row as any).projectedActualViaPlan ?? (row as any).projectedActualViaSale ?? (row as any).remainingProjectedSalesPlanComponent),
    }))
    .sort((a, b) => a.dayOfMonth - b.dayOfMonth || a.locationCode.localeCompare(b.locationCode) || a.classCode.localeCompare(b.classCode))

  return {
    rows,
  }
}

function aggregateDailyRows(rows: CurrentMonthAdvancedTrendDailyPoint[]): CurrentMonthAdvancedTrendDailyPoint[] {
  const byDay = new Map<number, CurrentMonthAdvancedTrendDailyPoint>()

  rows.forEach((row) => {
    const existing = byDay.get(row.dayOfMonth)
    if (!existing) {
      byDay.set(row.dayOfMonth, {
        locationCode: 'all',
        locationName: 'All locations',
        classCode: 'all',
        dayOfMonth: row.dayOfMonth,
        isElapsed: row.isElapsed,
        actualDaily: row.actualDaily,
        planDaily: row.planDaily,
        lastYearDaily: row.lastYearDaily,
        dailyProjected: row.dailyProjected,
        dailyProjectedViaPlan: row.dailyProjectedViaPlan,
      })
      return
    }

    existing.isElapsed = existing.isElapsed || row.isElapsed
    existing.actualDaily += row.actualDaily
    existing.planDaily += row.planDaily
    existing.lastYearDaily += row.lastYearDaily
    existing.dailyProjected += row.dailyProjected
    existing.dailyProjectedViaPlan += row.dailyProjectedViaPlan
  })

  return Array.from(byDay.values()).sort((a, b) => a.dayOfMonth - b.dayOfMonth)
}

function hasPlanDataInRows(rows: CurrentMonthAdvancedTrendDailyPoint[]) {
  return rows.some((row) => Math.abs(row.planDaily) > 0)
}

function hasLastYearDataInRows(rows: CurrentMonthAdvancedTrendDailyPoint[]) {
  return rows.some((row) => Math.abs(row.lastYearDaily) > 0)
}

// When there's no plan and no last-year data to inform a projection, fall back to a
// simple linear trend: hold the elapsed-to-date daily pace for the rest of the month.
function resolveLinearDailyRate(rows: CurrentMonthAdvancedTrendDailyPoint[], currentActualSales: number) {
  const daysElapsed = rows.filter((row) => row.isElapsed).length
  return daysElapsed > 0 ? currentActualSales / daysElapsed : 0
}

function buildTrendStats(rows: CurrentMonthAdvancedTrendDailyPoint[]) {
  const currentActualSales = rows.reduce((sum, row) => sum + (row.isElapsed ? row.actualDaily : 0), 0)
  const useLinearTrend = !hasPlanDataInRows(rows) && !hasLastYearDataInRows(rows)
  const linearDailyRate = useLinearTrend ? resolveLinearDailyRate(rows, currentActualSales) : 0

  const projectedSalesTotalActualsFromRows = rows.reduce((sum, row) => {
    if (row.isElapsed) return sum
    return sum + (useLinearTrend ? linearDailyRate : row.dailyProjected)
  }, 0)
  const projectedSalesTotalPlanFromRows = rows.reduce((sum, row) => sum + (row.isElapsed ? 0 : row.dailyProjectedViaPlan), 0)
  const projectedSalesTotalActuals = currentActualSales + projectedSalesTotalActualsFromRows
  const projectedSalesTotalPlan = currentActualSales + projectedSalesTotalPlanFromRows

  return {
    currentActualSales,
    projectedSalesTotalActuals,
    projectedSalesTotalPlan,
    advancedTrendActuals: projectedSalesTotalActuals > 0 ? currentActualSales / projectedSalesTotalActuals : null,
    advancedTrendPlan: projectedSalesTotalPlan > 0 ? currentActualSales / projectedSalesTotalPlan : null,
  }
}

function buildDynamicTrendChartData({
  dailyData,
  referenceDate,
  trendView,
  trendBasis,
}: {
  dailyData: CurrentMonthAdvancedTrendDailyPoint[];
  referenceDate: Date;
  trendView: TrendView;
  trendBasis: TrendBasis;
}): DynamicTrendPoint[] {
  if (!dailyData.length) return []

  const monthStart = startOfMonth(referenceDate)

  const currentActualSales = dailyData.reduce((sum, row) => sum + (row.isElapsed ? row.actualDaily : 0), 0)
  const useLinearTrend = !hasPlanDataInRows(dailyData) && !hasLastYearDataInRows(dailyData)
  const linearDailyRate = useLinearTrend ? resolveLinearDailyRate(dailyData, currentActualSales) : 0

  let cumulativeActual = 0
  let cumulativePlan = 0
  let cumulativeLastYear = 0
  let cumulativeProjected = 0

  const resolveLastYearComparisonDate = (thisYearDate: Date) => {
    const lastYearDate = new Date(
      thisYearDate.getFullYear() - 1,
      thisYearDate.getMonth(),
      thisYearDate.getDate(),
    )

    while (lastYearDate.getDay() !== thisYearDate.getDay()) {
      lastYearDate.setDate(lastYearDate.getDate() + 1)
    }

    return lastYearDate
  }

  return dailyData.map<DynamicTrendPoint>((row, index): DynamicTrendPoint => {
    const dayIndex = Math.max(row.dayOfMonth - 1, 0)
    const thisYearDate = addDays(monthStart, dayIndex)
    const lastYearDate = resolveLastYearComparisonDate(thisYearDate)
    const nextRow = dailyData[index + 1]
    const isLastElapsedPoint = row.isElapsed && (!nextRow || !nextRow.isElapsed)

    cumulativeActual += row.actualDaily
    cumulativePlan += row.planDaily
    cumulativeLastYear += row.lastYearDaily

    const projectedDaily = useLinearTrend
      ? linearDailyRate
      : (trendBasis === 'actuals' ? row.dailyProjected : row.dailyProjectedViaPlan)
    cumulativeProjected += row.isElapsed ? row.actualDaily : projectedDaily

    const point: DynamicTrendPoint = {
      day: row.dayOfMonth,
      dateLabel: format(thisYearDate, 'MMM d'),
      fullDateLabel: format(thisYearDate, 'EEE, MMM d'),
      fullDateLabelLastYear: format(lastYearDate, 'EEE, MMM d, yyyy'),
      isElapsed: row.isElapsed,
      actual: row.isElapsed ? (trendView === 'daily' ? row.actualDaily : cumulativeActual) : null,
      actualProjected: row.isElapsed
        ? (isLastElapsedPoint ? (trendView === 'daily' ? row.actualDaily : cumulativeActual) : null)
        : (trendView === 'daily' ? projectedDaily : cumulativeProjected),
      plan: trendView === 'daily' ? row.planDaily : cumulativePlan,
      lastYear: trendView === 'daily' ? row.lastYearDaily : cumulativeLastYear,
    }

    return point
  })
}

function CustomDynamicTrendTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null

  const point: DynamicTrendPoint = payload[0]?.payload
  if (!point) return null

  const thisYearValue = point.actual ?? point.actualProjected ?? 0
  const lastYearValue = point.lastYear
  const planValue = point.plan
  const hasLastYearData = Number(lastYearValue) !== 0
  const hasPlanData = Number(planValue) !== 0
  const vsLastYearPercent = hasLastYearData ? ((thisYearValue - lastYearValue) / lastYearValue) * 100 : null
  const vsPlanPercent = hasPlanData ? ((thisYearValue - planValue) / planValue) * 100 : null

  const planValueFormatted = PerformanceDashboardService.moneyFormat(planValue, true)

  return (
    <div className="min-w-[220px] rounded-xl bg-[#101828] px-4 py-3 shadow-xl">
      <div className={`grid gap-4 ${hasLastYearData ? 'grid-cols-2' : 'grid-cols-1'}`}>
        <div>
          <div className="text-[11px] font-medium text-[#98A2B3]">This Year</div>
          <div className="text-base font-semibold text-white">
            {PerformanceDashboardService.moneyFormat(thisYearValue, true)}
          </div>
          <div className="mt-0.5 text-[11px] text-[#98A2B3]">{point.fullDateLabel}</div>
        </div>
        {hasLastYearData && (
          <div>
            <div className="text-[11px] font-medium text-[#98A2B3]">Last Year</div>
            <div className="text-base font-semibold text-white">
              {PerformanceDashboardService.moneyFormat(lastYearValue, true)}
            </div>
            <div className="mt-0.5 text-[11px] text-[#98A2B3]">{point.fullDateLabelLastYear}</div>
          </div>
        )}
      </div>

      {(hasLastYearData || hasPlanData) && (
        <div className="mt-2.5 flex items-end justify-between gap-4 border-t border-white/10 pt-2.5">
          <div className="space-y-1">
            {vsLastYearPercent !== null && (
              <div className={`text-[12px] font-semibold ${vsLastYearPercent >= 0 ? 'text-[#75E0A7]' : 'text-[#FDA29B]'}`}>
                {formatSignedPercent(vsLastYearPercent)} vs LY
              </div>
            )}
            {vsPlanPercent !== null && (
              <div className={`text-[12px] font-semibold ${vsPlanPercent >= 0 ? 'text-[#75E0A7]' : 'text-[#FDA29B]'}`}>
                {formatSignedPercent(vsPlanPercent)} vs Plan
              </div>
            )}
          </div>
          {hasPlanData && (
            <div className="flex flex-col items-end">
              <div className="text-[11px] font-medium uppercase tracking-[0.04em] text-[#98A2B3]">
                Plan
              </div>
              <div className="text-[12px] font-semibold text-white">
                {planValueFormatted}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const LEGEND_ITEMS: Array<{ key: 'actual' | 'lastYear' | 'plan'; label: string; color: string }> = [
  { key: 'actual', label: 'This year (actual)', color: '#7F56D9' },
  { key: 'lastYear', label: 'Last year', color: '#F79009' },
  { key: 'plan', label: 'Plan', color: '#2E90FA' },
]

const DYNAMIC_TREND_TOOLTIP_SECTIONS: Array<{
  title: string;
  lead?: string;
  quote?: string;
  body: string;
}> = [
  {
    title: 'Trend By Actuals',
    lead: 'Use this when you want to know:',
    quote: 'How is my business really performing compared to last year?',
    body: 'It ignores your plan entirely and measures pure momentum.',
  },
  {
    title: 'Trend By Plan',
    quote: 'Am I going to hit my number?',
    body: 'It measures execution against the target you committed to, including events your plan already accounts for, like a promo or trunk show later this month.',
  },
  {
    title: 'The difference',
    body: 'Actuals answers "how am I trending vs last year?" and Plan answers "am I on track to hit my target?" They can disagree, and that is the point. If your plan calls for a big event last year did not have, Trend by Plan knows it is coming and Trend by Actuals does not. If last year had a late-month surge you expect to repeat, Trend by Actuals captures it. When both agree, you can trust the number. When they split, look at what is different about this month.',
  },
]

export default function SalesDynamicTrendModal({
  open,
  onOpenChange,
  clientId,
  epochMonth = null,
  monthLabel,
  locationOptions = DEFAULT_LOCATION_OPTIONS,
}: SalesDynamicTrendModalProps) {
  const [trendBasis, setTrendBasis] = React.useState<TrendBasis>('plan')
  const [trendView, setTrendView] = React.useState<TrendView>('cumulative')
  const [selectedLocation, setSelectedLocation] = React.useState(locationOptions[0]?.value ?? 'all')
  const [trendPayload, setTrendPayload] = React.useState<CurrentMonthAdvancedTrendPayload | null>(null)
  const [trendLoading, setTrendLoading] = React.useState(false)
  const [hiddenSeries, setHiddenSeries] = React.useState<Record<'actual' | 'lastYear' | 'plan', boolean>>({
    actual: false,
    lastYear: false,
    plan: false,
  })

  const resolvedLocationOptions = React.useMemo(() => {
    const rows = trendPayload?.rows || []
    if (!rows.length) return locationOptions

    const byCode = new Map<string, DynamicTrendLocationOption>()
    rows.forEach((row) => {
      if (!row.locationCode) return
      const label = row.locationName ? `${row.locationCode} ${row.locationName}` : row.locationCode
      if (!byCode.has(row.locationCode)) {
        byCode.set(row.locationCode, { value: row.locationCode, label })
      }
    })

    const dynamicOptions = Array.from(byCode.values()).sort((a, b) => a.label.localeCompare(b.label))
    return [{ value: 'all', label: 'All locations' }, ...dynamicOptions]
  }, [locationOptions, trendPayload?.rows])

  React.useEffect(() => {
    if (!resolvedLocationOptions.some((option) => option.value === selectedLocation)) {
      setSelectedLocation('all')
    }
  }, [resolvedLocationOptions, selectedLocation])

  React.useEffect(() => {
    if (!open || !clientId) {
      setTrendPayload(null)
      setTrendLoading(false)
      return
    }

    let cancelled = false
    setTrendLoading(true)

    ReportService.getCurrentMonthAdvancedTrendData({
      clientId,
      epochMonth,
    }).then((response) => {
      if (cancelled) return
      const payload = resolveCurrentMonthAdvancedTrendPayload(response)
      setTrendPayload(payload)
    }).catch((error) => {
      console.error('Failed to fetch current month advanced trend data', error)
      if (!cancelled) setTrendPayload(null)
    }).finally(() => {
      if (!cancelled) setTrendLoading(false)
    })

    return () => { cancelled = true }
  }, [clientId, epochMonth, open])

  const referenceDate = React.useMemo(() => new Date(), [])
  const resolvedMonthLabel = monthLabel ?? format(referenceDate, 'MMMM yyyy')
  const filteredRows = React.useMemo(() => {
    const rows = trendPayload?.rows || []
    if (selectedLocation === 'all') return rows
    return rows.filter((row) => row.locationCode === selectedLocation)
  }, [selectedLocation, trendPayload?.rows])
  const filteredTrendStats = React.useMemo(() => buildTrendStats(filteredRows), [filteredRows])
  const filteredDailyData = React.useMemo(() => aggregateDailyRows(filteredRows), [filteredRows])
  const hasPlanData = React.useMemo(() => hasPlanDataInRows(filteredDailyData), [filteredDailyData])
  const hasLastYearData = React.useMemo(() => hasLastYearDataInRows(filteredDailyData), [filteredDailyData])

  React.useEffect(() => {
    if (!hasPlanData && trendBasis === 'plan') {
      setTrendBasis('actuals')
    }
  }, [hasPlanData, trendBasis])

  const selectedProjectedTotal = trendBasis === 'actuals'
    ? filteredTrendStats.projectedSalesTotalActuals
    : filteredTrendStats.projectedSalesTotalPlan
  const cumulativeChartData = React.useMemo(() => buildDynamicTrendChartData({
    dailyData: filteredDailyData,
    referenceDate,
    trendView: 'cumulative',
    trendBasis,
  }), [filteredDailyData, referenceDate, trendBasis])
  const chartData = React.useMemo(() => buildDynamicTrendChartData({
    dailyData: filteredDailyData,
    referenceDate,
    trendView,
    trendBasis,
  }), [filteredDailyData, referenceDate, trendBasis, trendView])
  const dataThroughPoint = React.useMemo(
    () => [...chartData].reverse().find((point) => point.isElapsed) || chartData[chartData.length - 1],
    [chartData],
  )
  const trendSalesTotal = selectedProjectedTotal
  const monthlyPlanTotal = cumulativeChartData[cumulativeChartData.length - 1]?.plan ?? 0
  const lastYearTotal = cumulativeChartData[cumulativeChartData.length - 1]?.lastYear ?? 0
  const pacingPercent = monthlyPlanTotal > 0 ? Math.round((trendSalesTotal / monthlyPlanTotal) * 100) : null
  const vsLastYearTotalPercent = lastYearTotal > 0 ? ((trendSalesTotal - lastYearTotal) / lastYearTotal) * 100 : 0
  const vsPlanTotalPercent = monthlyPlanTotal > 0 ? ((trendSalesTotal - monthlyPlanTotal) / monthlyPlanTotal) * 100 : 0
  const totalShortfall = Math.max(monthlyPlanTotal - trendSalesTotal, 0)

  const toggleSeriesVisibility = (key: 'actual' | 'lastYear' | 'plan') => {
    setHiddenSeries((current) => ({ ...current, [key]: !current[key] }))
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!gap-0 max-h-[82vh] max-w-[88vw] overflow-y-auto !rounded-[16px] border border-[#E4E7EC] bg-white p-3 sm:max-w-[920px]">
        <DialogHeader className="space-y-2">
          <div className="flex items-center gap-3">
            <DialogTitle className="!text-[20px] !font-semibold !leading-[1.2] !tracking-[-0.02em] !text-[#101828] sm:!text-[22px]">
              Dynamic sales trend · {resolvedMonthLabel}
            </DialogTitle>
            <Badge variant="Override" className="inline-flex -translate-y-[2px] items-center self-center !rounded-full !border-0 !bg-[#F2F4F7] !px-2 !py-0.5 !text-[13px] !font-medium !leading-[1.2] !text-[#344054]">
              Beta
            </Badge>
          </div>
          <p className="text-left !text-[12px] !font-normal !leading-[1.45] !text-[#101828] sm:!text-[13px]">
            {pacingPercent !== null
              ? `You're pacing at ${pacingPercent}% of plan so far; holding that pace, this is where you'll land against your monthly target.`
              : "Holding your current pace, this is where you'll land against your monthly target."}
          </p>
        </DialogHeader>

        <div className="mt-1.5 space-y-1">
          <div className="inline-flex items-center gap-1.5 text-[11px] font-normal text-[#98A2B3] sm:text-[12px]">
              <span>Dynamic trend by:</span>
              <HoverTooltipProvider delayDuration={0}>
                <HoverTooltip>
                  <HoverTooltipTrigger asChild>
                    <button
                      type="button"
                      aria-label="About dynamic trend options"
                      className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[#98A2B3] hover:text-[#667085] focus:outline-none"
                    >
                      <CircleHelp className="h-3.5 w-3.5" />
                    </button>
                  </HoverTooltipTrigger>
                  <HoverTooltipContent
                    side="bottom"
                    align="start"
                    sideOffset={8}
                    collisionPadding={16}
                    className="z-[1600] w-[360px] rounded-xl border border-[#344054] bg-[#101828] px-3 py-3 text-left text-[12px] leading-[1.5] text-[#E4E7EC] shadow-xl"
                  >
                    <div className="space-y-3">
                      {DYNAMIC_TREND_TOOLTIP_SECTIONS.map((section) => (
                        <div key={section.title} className="space-y-1">
                          <div>
                            <span className="font-semibold text-white">{section.title}</span>
                            {section.lead && <span className="text-[#D0D5DD]"> - {section.lead}</span>}
                            {section.quote && <span className="font-medium text-white"> {`"${section.quote}"`}</span>}
                          </div>
                          <p className="text-[#E4E7EC]">{section.body}</p>
                        </div>
                      ))}
                    </div>
                  </HoverTooltipContent>
                </HoverTooltip>
              </HoverTooltipProvider>
          </div>

          <div className="relative z-20 flex flex-wrap items-center gap-2.5 lg:flex-nowrap lg:justify-between">
            <div className="flex flex-wrap items-center gap-2.5 sm:flex-nowrap">
              <ToggleGroup
                type="single"
                value={trendBasis}
                onValueChange={(value) => value && setTrendBasis(value as TrendBasis)}
                className="rounded-xl border border-[#E4E7EC] bg-[#F9FAFB] p-0.5"
              >
                <ToggleGroupItem
                  value="actuals"
                  className="h-8 rounded-lg bg-transparent px-2.5 text-[13px] font-medium text-[#667085] hover:bg-transparent hover:text-[#344054] data-[state=on]:bg-white data-[state=on]:text-[#6941C6] data-[state=on]:font-semibold data-[state=on]:shadow-sm"
                >
                  Actuals
                </ToggleGroupItem>
                <ToggleGroupItem
                  value="plan"
                  disabled={!hasPlanData}
                  className="h-8 rounded-lg bg-transparent px-2.5 text-[13px] font-medium text-[#667085] hover:bg-transparent hover:text-[#344054] data-[state=on]:bg-white data-[state=on]:text-[#6941C6] data-[state=on]:font-semibold data-[state=on]:shadow-sm disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Plan
                </ToggleGroupItem>
              </ToggleGroup>

              <ToggleGroup
                type="single"
                value={trendView}
                onValueChange={(value) => value && setTrendView(value as TrendView)}
                className="rounded-xl border border-[#E4E7EC] bg-[#F9FAFB] p-0.5"
              >
                <ToggleGroupItem
                  value="cumulative"
                  className="h-8 rounded-lg bg-transparent px-2.5 text-[13px] font-medium text-[#667085] hover:bg-transparent hover:text-[#344054] data-[state=on]:bg-white data-[state=on]:text-[#6941C6] data-[state=on]:font-semibold data-[state=on]:shadow-sm"
                >
                  Cumulative
                </ToggleGroupItem>
                <ToggleGroupItem
                  value="daily"
                  className="h-8 rounded-lg bg-transparent px-2.5 text-[13px] font-medium text-[#667085] hover:bg-transparent hover:text-[#344054] data-[state=on]:bg-white data-[state=on]:text-[#6941C6] data-[state=on]:font-semibold data-[state=on]:shadow-sm"
                >
                  Daily
                </ToggleGroupItem>
              </ToggleGroup>

              <div className="relative z-[2600] isolate">
                <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                  <SelectTrigger className="h-8 w-[150px] rounded-xl border-[#D0D5DD] bg-white text-[13px] font-medium text-[#344054] shadow-sm [&>span]:truncate [&>span]:pr-5">
                    <SelectValue placeholder="Location" className="truncate" />
                  </SelectTrigger>
                  <SelectContent className="z-[4000] border-[#D0D5DD] bg-white shadow-xl">
                    {resolvedLocationOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex flex-col items-start gap-1 lg:ml-auto lg:items-end">
              <div className="flex flex-wrap items-center gap-3 lg:justify-end">
                {LEGEND_ITEMS.filter((item) => {
                  if (item.key === 'plan') return hasPlanData
                  if (item.key === 'lastYear') return hasLastYearData
                  return true
                }).map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => toggleSeriesVisibility(item.key)}
                    className={`flex items-center gap-2 transition-opacity ${hiddenSeries[item.key] ? 'opacity-40' : 'opacity-100'}`}
                  >
                    <span className="h-[2px] w-6 rounded-full" style={{ backgroundColor: item.color }} />
                    <span className="text-[13px] font-normal text-[#344054]">{item.label}</span>
                  </button>
                ))}
              </div>
              <div className="mt-0.5 flex items-center gap-2 text-[11px] font-normal text-[#344054]">
                <span className="block h-0 w-6 border-t-2 border-dashed border-[#7F56D9]" />
                <span>Dynamic trended sales</span>
              </div>
              <span className="text-[11px] text-[#98A2B3]">Click a line in the key to show or hide it.</span>
            </div>
          </div>
        </div>

        <div className="relative z-0 mt-1.5 h-[214px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 18, right: 34, left: 10, bottom: 8 }}>
              <CartesianGrid stroke="#EEF1F5" vertical={false} />
              <XAxis
                dataKey="dateLabel"
                tick={{ fill: '#667085', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                tickMargin={10}
                padding={{ left: 4, right: 16 }}
                ticks={[chartData[0]?.dateLabel, chartData[9]?.dateLabel, chartData[19]?.dateLabel, chartData[chartData.length - 1]?.dateLabel].filter(Boolean)}
                interval={0}
              />
              <YAxis
                hide={trendView !== 'daily'}
                domain={[0, (dataMax: number) => dataMax * 1.08]}
                tick={{ fill: '#667085', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(value) => PerformanceDashboardService.moneyFormat(Number(value) || 0, true)}
              />
              <Tooltip content={<CustomDynamicTrendTooltip />} cursor={{ stroke: '#D0D5DD', strokeDasharray: '4 4' }} />

              {dataThroughPoint && (
                <ReferenceLine
                  x={dataThroughPoint.dateLabel}
                  stroke="#98A2B3"
                  strokeDasharray="4 4"
                  label={{ value: 'Data Through', position: 'top', fill: '#667085', fontSize: 11 }}
                />
              )}

              <Line
                type="monotone"
                dataKey="lastYear"
                stroke="#F79009"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, strokeWidth: 0, fill: '#F79009' }}
                name="Last year"
                connectNulls
                hide={hiddenSeries.lastYear || !hasLastYearData}
              />
              <Line
                type="monotone"
                dataKey="plan"
                stroke="#2E90FA"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, strokeWidth: 0, fill: '#2E90FA' }}
                name="Plan"
                connectNulls
                hide={hiddenSeries.plan || !hasPlanData}
              />
              <Line
                type="monotone"
                dataKey="actual"
                stroke="#7F56D9"
                strokeWidth={2.5}
                dot={false}
                activeDot={{ r: 4, strokeWidth: 0, fill: '#7F56D9' }}
                name="This year (actual)"
                connectNulls
                hide={hiddenSeries.actual}
              />
              <Line
                type="monotone"
                dataKey="actualProjected"
                stroke="#7F56D9"
                strokeWidth={2.5}
                strokeDasharray="5 5"
                dot={false}
                activeDot={{ r: 4, strokeWidth: 0, fill: '#7F56D9' }}
                name="This year (projected)"
                legendType="none"
                connectNulls
                hide={hiddenSeries.actual}
              />

              {dataThroughPoint && (
                <ReferenceDot
                  x={dataThroughPoint.dateLabel}
                  y={dataThroughPoint.actualProjected ?? dataThroughPoint.actual ?? 0}
                  r={5}
                  fill="#7F56D9"
                  stroke="#FFFFFF"
                  strokeWidth={2}
                  isFront
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="mt-1 grid grid-cols-1 gap-2 sm:grid-cols-3">
          <div className="rounded-xl bg-[#F8FAFC] px-3 py-3">
            <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#98A2B3]">Trend sales</div>
            <div className="mt-1 text-[18px] font-bold leading-none text-[#101828]">
              {trendLoading ? 'Loading…' : PerformanceDashboardService.moneyFormat(trendSalesTotal, true)}
            </div>
            {hasLastYearData && (
              <div className={`mt-1.5 text-[12px] font-medium ${vsLastYearTotalPercent >= 0 ? 'text-[#079455]' : 'text-[#D92D20]'}`}>
                {formatSignedPercent(vsLastYearTotalPercent)} vs LY
              </div>
            )}
            {hasPlanData && (
              <div className={`text-[12px] font-medium ${vsPlanTotalPercent >= 0 ? 'text-[#079455]' : 'text-[#D92D20]'}`}>
                {formatSignedPercent(vsPlanTotalPercent)} vs Plan
              </div>
            )}
          </div>

          <div className="rounded-xl bg-[#F8FAFC] px-3 py-3">
            <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#98A2B3]">Monthly plan</div>
            <div className="mt-1 text-[18px] font-bold leading-none text-[#101828]">
              {trendLoading ? 'Loading…' : hasPlanData ? PerformanceDashboardService.moneyFormat(monthlyPlanTotal, true) : '—'}
            </div>
          </div>

          <div className="rounded-xl bg-[#F8FAFC] px-3 py-3">
            <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#98A2B3]">Total shortfall</div>
            <div className="mt-1 text-[18px] font-bold leading-none text-[#101828]">
              {trendLoading ? 'Loading…' : hasPlanData ? PerformanceDashboardService.moneyFormat(totalShortfall, true) : '—'}
            </div>
          </div>
        </div>

        <div className="mt-1 border-t border-[#EAECF0] pt-1">
          <p className="text-[11px] font-normal text-[#98A2B3]">
            This view is in beta while we finalize daily data accuracy — figures here may be revised.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  )
}
